import { useState } from 'react';
import {
  type AppointmentType,
  type ExpandableFieldItem,
  type SchedulingData,
} from '@features/appointment-types';
import { type Appointment as AppointmentResponse } from '@features/appointments';

import { formatAppointmentDuration } from '../../../utils/durationUtils';
import {
  findScheduledActionByRuleId,
  formatActionTimestamp,
  mapScheduledActionStatus,
} from '../utils/scheduledActionUtils';

export interface UseFormsAndRemindersProps {
  initialAppointment?: AppointmentResponse;
}

export const useFormsAndReminders = ({
  initialAppointment,
}: UseFormsAndRemindersProps = {}) => {
  const initialAppointmentType =
    initialAppointment?.encounter_type && initialAppointment.encounter_type_id
      ? {
          encounter_type_id: initialAppointment.encounter_type_id,
          encounter_type: initialAppointment.encounter_type,
          provider_id: initialAppointment.provider_id || '',
          color: initialAppointment.color || 'color1', // Default to color1 if not provided
        }
      : undefined;

  // State
  const [selectedForms, setSelectedForms] = useState<ExpandableFieldItem[]>([]);
  const [selectedReminders, setSelectedReminders] = useState<ExpandableFieldItem[]>([]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<
    AppointmentType | undefined
  >(initialAppointmentType);

  // Form handlers
  const handleAddForm = (formData: SchedulingData) => {
    const newForm: ExpandableFieldItem = {
      id: Date.now().toString(),
      name: formData.name,
      timing: formData.timing,
      offset: formData.customTiming || '',
      formId: formData.formId,
      status: 'configured',
    };
    setSelectedForms(prev => [...prev, newForm]);
  };

  const handleUpdateForm = (formId: string | number, formData: SchedulingData) => {
    setSelectedForms(prev =>
      prev.map(form =>
        form.id === formId
          ? {
              ...form,
              name: formData.name,
              timing: formData.timing,
              offset: formData.customTiming || '',
              formId: formData.formId,
            }
          : form,
      ),
    );
  };

  const handleRemoveForm = (formId: string | number) => {
    setSelectedForms(prev => prev.filter(form => form.id !== formId));
  };

  // Reminder handlers
  const handleAddReminder = (reminderData: SchedulingData) => {
    const newReminder: ExpandableFieldItem = {
      id: Date.now().toString(),
      name: reminderData.name,
      timing: reminderData.timing,
      offset: reminderData.customTiming || '',
      status: 'configured',
    };
    setSelectedReminders(prev => [...prev, newReminder]);
  };

  const handleUpdateReminder = (reminderId: string | number, reminderData: SchedulingData) => {
    setSelectedReminders(prev =>
      prev.map(reminder =>
        reminder.id === reminderId
          ? {
              ...reminder,
              name: reminderData.name,
              timing: reminderData.timing,
              offset: reminderData.customTiming || '',
            }
          : reminder,
      ),
    );
  };

  const handleRemoveReminder = (reminderId: string | number) => {
    setSelectedReminders(prev => prev.filter(reminder => reminder.id !== reminderId));
  };

  // Appointment type handler
  const handleAppointmentTypeSelect = (appointmentType: AppointmentType | undefined) => {
    setSelectedAppointmentType(appointmentType);
  };

  // Helper function to populate forms from appointment type defaults
  const populateFormsFromDefaults = (appointmentType: AppointmentType) => {
    if (appointmentType.default_form_rules) {
      const formItems: ExpandableFieldItem[] = appointmentType.default_form_rules.map(
        (rule, index) => {
          const offset = rule.offset || '';
          let timing: string;
          try {
            timing = formatAppointmentDuration(offset);
          } catch (error) {
            console.warn(`Invalid offset format for rule ${index}:`, offset);
            timing = offset || 'at appointment time';
          }

          return {
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            timing: timing,
            offset: offset,
            formId: rule.form_definition_id,
            status: 'configured',
          };
        },
      );
      setSelectedForms(formItems);
    } else {
      setSelectedForms([]);
    }
  };

  // Helper function to populate reminders from appointment type defaults
  const populateRemindersFromDefaults = (appointmentType: AppointmentType) => {
    if (appointmentType.default_reminder_rules) {
      const reminderItems: ExpandableFieldItem[] = appointmentType.default_reminder_rules.map(
        (rule, index) => {
          const offset = rule.offset || '';
          let timing: string;
          try {
            timing = formatAppointmentDuration(offset);
          } catch (error) {
            console.warn(`Invalid offset format for reminder ${index}:`, offset);
            timing = offset || 'at appointment time';
          }

          return {
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            timing: timing,
            offset: offset,
            status: 'configured',
          };
        },
      );
      setSelectedReminders(reminderItems);
    } else {
      setSelectedReminders([]);
    }
  };

  // Helper function to populate forms from appointment data
  const populateFormsFromAppointment = (appointment: AppointmentResponse) => {
    if (appointment.form_rules) {
      const formItems: ExpandableFieldItem[] = appointment.form_rules.map((rule, index) => {
        const offset = rule.offset || '';
        let timing: string;
        try {
          timing = formatAppointmentDuration(offset);
        } catch (error) {
          console.warn(`Invalid offset format for form ${index}:`, offset);
          timing = offset || 'at appointment time';
        }

        // Find matching scheduled action
        const scheduledAction = rule.id
          ? findScheduledActionByRuleId(appointment.scheduled_actions, rule.id)
          : undefined;

        return {
          id: `form-${index}`,
          name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
          timing: timing,
          offset: offset,
          formId: rule.form_definition_id,
          ruleId: rule.id,
          status: scheduledAction
            ? mapScheduledActionStatus(scheduledAction.status, 'appointment_form')
            : 'configured',
          scheduledActionStatus: scheduledAction?.status,
          errorMessage: scheduledAction?.error_message || undefined,
          executedAt: scheduledAction?.executed_at || undefined,
          scheduledAt: scheduledAction?.scheduled_at,
          scheduledFor: scheduledAction?.scheduled_at
            ? formatActionTimestamp(scheduledAction.scheduled_at)
            : undefined,
        };
      });
      setSelectedForms(formItems);
    } else {
      setSelectedForms([]);
    }
  };

  // Helper function to populate reminders from appointment data
  const populateRemindersFromAppointment = (appointment: AppointmentResponse) => {
    if (appointment.reminder_rules) {
      const reminderItems: ExpandableFieldItem[] = appointment.reminder_rules.map((rule, index) => {
        const offset = rule.offset || '';
        let timing: string;
        try {
          timing = formatAppointmentDuration(offset);
        } catch (error) {
          console.warn(`Invalid offset format for reminder ${index}:`, offset);
          timing = offset || 'at appointment time';
        }

        // Find matching scheduled action
        const scheduledAction = rule.id
          ? findScheduledActionByRuleId(appointment.scheduled_actions, rule.id)
          : undefined;

        return {
          id: `reminder-${index}`,
          name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
          timing: timing,
          offset: offset,
          ruleId: rule.id,
          status: scheduledAction
            ? mapScheduledActionStatus(scheduledAction.status, 'appointment_reminder')
            : 'configured',
          scheduledActionStatus: scheduledAction?.status,
          errorMessage: scheduledAction?.error_message || undefined,
          executedAt: scheduledAction?.executed_at || undefined,
          scheduledAt: scheduledAction?.scheduled_at,
          scheduledFor: scheduledAction?.scheduled_at
            ? formatActionTimestamp(scheduledAction.scheduled_at)
            : undefined,
        };
      });
      setSelectedReminders(reminderItems);
    } else {
      setSelectedReminders([]);
    }
  };

  return {
    // State
    selectedForms,
    selectedReminders,
    selectedAppointmentType,

    // Handlers
    handleAddForm,
    handleUpdateForm,
    handleRemoveForm,
    handleAddReminder,
    handleUpdateReminder,
    handleRemoveReminder,
    handleAppointmentTypeSelect,

    // Utilities
    populateFormsFromDefaults,
    populateRemindersFromDefaults,
    populateFormsFromAppointment,
    populateRemindersFromAppointment,
  };
};

export type UseFormsAndRemindersReturn = ReturnType<typeof useFormsAndReminders>;
